import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, catchError, debounceTime, distinctUntilChanged, finalize, of } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { AppPrimeTable, PrimeTableColumn } from '../../../../shared/components/prime-table/prime-table';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { AppDropdown } from '../../../../shared/components/dropdown/dropdown';
import { FinancialService } from '../../../../core/services/financial.service';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { EditHealthPlanProcedureModalComponent } from '../edit-health-plan-procedure-modal/edit-health-plan-procedure-modal.component';
import { ImportHealthPlanProceduresModalComponent } from '../import-health-plan-procedures-modal/import-health-plan-procedures-modal.component';

/** Per-health-plan procedures/fee-table screen — Angular port of the
 *  FlutterFlow HealthPlanPageWidget (list + import + edit + delete). */
@Component({
  selector: 'app-health-plan-procedures-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    AppPrimeTable,
    AppButton,
    AppDropdown,
    HasPermissionDirective,
    EditHealthPlanProcedureModalComponent,
    ImportHealthPlanProceduresModalComponent,
  ],
  templateUrl: './health-plan-procedures-modal.component.html',
})
export class HealthPlanProceduresModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() healthPlanId: number | null = null;
  @Input() healthPlanName = '';
  @Input() branchId: number | null = null;
  @Output() close = new EventEmitter<void>();

  @ViewChild('statusTemplate', { static: true }) statusTemplate!: TemplateRef<any>;
  @ViewChild('actionsTemplate', { static: true }) actionsTemplate!: TemplateRef<any>;

  procedures: any[] = [];
  loading = false;
  totalRecords = 0;
  rows = 10;
  first = 0;
  searchText = '';

  showImportModal = false;
  showEditModal = false;
  editingProcedure: any | null = null;
  deletingProcedure: any | null = null;
  deleting = false;

  actionItems: MenuItem[] = [];
  templates: { [key: string]: TemplateRef<any> } = {};

  /** The list RPC only ever projects this subset (description/subfamily/base_price/active) —
   *  confirmed against a live response and matches the source FlutterFlow list exactly.
   *  family/internal_code/group/subgroup/percent are edit-only, fetched fresh by id. */
  columns: PrimeTableColumn[] = [
    { field: 'description', header: 'health_plan_procedures_table_description', type: 'custom' },
    { field: 'subfamily', header: 'health_plan_procedures_table_subfamily', type: 'custom' },
    { field: 'base_price', header: 'health_plan_procedures_table_value', type: 'currency', styleClass: '!text-right' },
    { field: 'active', header: 'health_plan_procedures_table_status', type: 'custom', styleClass: '!text-center' },
    { field: 'actions', header: 'health_plan_procedures_table_actions', type: 'custom', styleClass: '!text-center' },
  ];

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  constructor(
    private financialService: FinancialService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((text) => {
        this.searchText = text;
        this.first = 0;
        this.loadData();
      });
  }

  ngOnInit() {
    this.templates = {
      active: this.statusTemplate,
      actions: this.actionsTemplate,
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen'] && this.isOpen) {
      this.first = 0;
      this.searchText = '';
      this.loadData();
    }
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
    this.searchSubject.complete();
  }

  onSearch(text: string) {
    this.searchSubject.next(text);
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
    this.loadData();
  }

  loadData() {
    if (!this.healthPlanId || !this.branchId) return;

    this.loading = true;
    this.cdr.detectChanges();

    this.financialService
      .getHealthPlanProcedures({
        entityId: this.healthPlanId,
        branchesId: this.branchId,
        description: this.searchText,
        limit: this.rows,
        offset: this.first,
      })
      .pipe(
        catchError((err) => {
          console.error('Error loading health plan procedures', err);
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe((res: any) => {
        const list = Array.isArray(res) ? res : res?.procedures || [];
        this.procedures = list || [];

        // The RPC only ever returns total_pages, not an exact row count, so
        // total_pages * rows overshoots whenever the last page is partial
        // (e.g. 2 rows / 1 page reports "10 records"). Once we've reached the
        // last page we know the exact count from what came back; otherwise
        // fall back to the total_pages estimate just to size the paginator.
        if (res?.total_records !== undefined) {
          this.totalRecords = Number(res.total_records);
        } else if (res?.total_pages !== undefined) {
          const totalPages = Number(res.total_pages) || 1;
          const currentPageIndex = Math.floor(this.first / this.rows);
          const isLastPage = currentPageIndex >= totalPages - 1;
          this.totalRecords = isLastPage ? this.first + this.procedures.length : totalPages * this.rows;
        } else {
          this.totalRecords = this.procedures.length;
        }
      });
  }

  getItemActions(row: any): MenuItem[] {
    return [
      {
        label: this.translate.instant('action_edit'),
        icon: 'pi pi-pencil',
        command: () => this.openEdit(row),
      },
      {
        label: this.translate.instant('action_delete'),
        icon: 'pi pi-trash',
        command: () => this.confirmDelete(row),
      },
    ];
  }

  toggleMenu(row: any) {
    this.actionItems = this.getItemActions(row);
  }

  openImport() {
    this.showImportModal = true;
  }

  onImportClose(success?: boolean) {
    this.showImportModal = false;
    if (success) this.loadData();
  }

  openEdit(row: any) {
    if (!row?.id) return;

    // The list row is a lean projection (description/subfamily/base_price/active
    // only) — fetch the full record so family/internal_code/group/subgroup aren't
    // silently blanked out when the edit form is saved.
    this.financialService.getHealthPlanProcedureById(row.id).subscribe({
      next: (fullRow) => {
        this.editingProcedure = fullRow || row;
        this.showEditModal = true;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading health plan procedure details:', err);
        this.editingProcedure = row;
        this.showEditModal = true;
        this.cdr.detectChanges();
      },
    });
  }

  onEditClose() {
    this.showEditModal = false;
    this.editingProcedure = null;
  }

  onEditSaved() {
    this.onEditClose();
    this.loadData();
  }

  confirmDelete(row: any) {
    this.deletingProcedure = row;
  }

  cancelDelete() {
    this.deletingProcedure = null;
  }

  deleteConfirmed() {
    if (!this.deletingProcedure?.id) return;

    this.deleting = true;
    this.financialService.deleteHealthPlanProcedure(this.deletingProcedure.id).subscribe({
      next: () => {
        this.deleting = false;
        this.deletingProcedure = null;
        this.loadData();
      },
      error: (err) => {
        console.error('Error deleting health plan procedure:', err);
        this.deleting = false;
      },
    });
  }

  onClose() {
    this.close.emit();
  }
}
